import { Box, Button, InputBase, SxProps } from '@mui/material';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { useEffect, useMemo, useState } from 'react';
import { IStyles } from '../../../commonUtils/styleUtils';

export type QuantityButtonV2Props = {
  value: number;
  onChange?: (nextValue: number) => void;
  onIncrement?: () => void;
  onDecrement?: () => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  rootStyles?: SxProps;
  highlightWhenFilled?: boolean;
  stepButtonColor?: string /** Overrides - / + color when a value is set. */;
  size?: 'default' | 'compact';
  showStepperWhenZero?: boolean /** Keep the - / value / + stepper visible when value is 0 instead of the Add button. */;
  decrementDataId?: string;
  incrementDataId?: string;
  addDataId?: string;
  inputDataId?: string;
};

export const QuantityButtonV2 = (props: QuantityButtonV2Props) => {
  const {
    value,
    onChange,
    onIncrement,
    onDecrement,
    min,
    max,
    disabled,
    rootStyles,
    highlightWhenFilled,
    stepButtonColor,
    size = 'default',
    showStepperWhenZero,
    decrementDataId,
    incrementDataId,
    addDataId,
    inputDataId,
  } = props;

  // ----------------------------------------------------

  const isCompact = size === 'compact';
  const FONT_SIZE = isCompact
    ? { xs: 14, md: 16, lg: 16 }
    : { xs: 18, md: 35, lg: 26 };

  const styles: IStyles = {
    root: {
      width: isCompact
        ? { xs: '100%', md: pxTovW(148), lg: pxTovW(132) }
        : { xs: '100%', md: pxTovW(170), lg: pxTovW(150) },
      height: isCompact
        ? { xs: pxToRem(36), md: pxTovW(42), lg: pxTovW(40) }
        : { xs: pxToRem(50), md: pxTovW(75), lg: pxTovW(60) },
      boxShadow: { xs: '0px 3px 6px #0000000A', md: '0px 3px 6px #0000000A' },
      border: '1px solid #007CDC66',
      borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden',
      backgroundColor: '#fff',
      boxSizing: 'border-box',
      paddingX: { xs: pxToRem(0), md: pxTovW(0) },
    },
    addButton: {
      width: '100%',
      height: '100%',
      borderRadius: 0,
      boxShadow: 'none',
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
      textTransform: 'none',
    },
    stepButton: {
      width: isCompact
        ? { xs: pxToRem(32), md: pxTovW(28) }
        : { xs: pxToRem(40), md: pxTovW(35) },
      height: '100%',
      minWidth: isCompact
        ? { xs: pxToRem(32), md: pxTovW(28) }
        : { xs: pxToRem(40), md: pxTovW(35) },
      flexShrink: 0,
      padding: 0,
      borderRadius: 0,
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
    },
    inputWrapper: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: '6px',
    },
    inputBase: {
      width: '100%',
      textAlign: 'center',
      fontSize: {
        xs: pxToRem(FONT_SIZE.xs),
        md: pxTovW(FONT_SIZE.md),
        lg: pxTovW(FONT_SIZE.lg),
      },
      fontWeight: 600,
      '& input': {
        textAlign: 'center',
        fontSize: {
          xs: pxToRem(FONT_SIZE.xs),
          md: pxTovW(FONT_SIZE.md),
          lg: pxTovW(FONT_SIZE.lg),
        },
        fontWeight: 600,
        padding: 0,
      },
      '& input::placeholder': {
        opacity: 1,
        color: 'secondary.main',
      },
    },
  };

  // ----------------------------------------------------

  const [draft, setDraft] = useState<string>(() =>
    value === 0 && !showStepperWhenZero ? '' : String(value)
  );
  const effectiveMin = useMemo(
    () => (typeof min === 'number' ? min : 0),
    [min]
  );
  const effectiveMax = useMemo(
    () => (typeof max === 'number' ? max : undefined),
    [max]
  );
  const canDecrement = !disabled && value > effectiveMin;
  const canIncrement =
    !disabled && (typeof effectiveMax !== 'number' || value < effectiveMax);

  // ----------------------------------------------------

  const clamp = (n: number) => {
    let next = Number.isFinite(n) ? Math.trunc(n) : 0;
    next = Math.max(0, next);
    next = Math.max(effectiveMin, next);
    if (typeof effectiveMax === 'number') next = Math.min(effectiveMax, next);
    return next;
  };

  // ----------------------------------------------------

  const setDraftFromValue = (nextValue: number) => {
    setDraft(nextValue === 0 && !showStepperWhenZero ? '' : String(nextValue));
  };

  // ----------------------------------------------------

  const commit = (raw: string) => {
    const nextValue = raw === '' ? 0 : clamp(parseInt(raw, 10));
    setDraftFromValue(nextValue);
    if (nextValue !== value) onChange?.(nextValue);
  };

  // ----------------------------------------------------

  const handleDraftChange = (next: string) => {
    if (next === '') {
      setDraft('');
      return;
    }
    if (!/^\d+$/.test(next)) return;

    const parsed = parseInt(next, 10);
    if (typeof effectiveMax === 'number' && parsed > effectiveMax) {
      setDraftFromValue(effectiveMax);
      if (effectiveMax !== value) onChange?.(effectiveMax);
      return;
    }

    setDraft(next);
  };

  // ----------------------------------------------------

  const handleIncrement = () => {
    if (disabled || !canIncrement) return;
    if (onIncrement) {
      onIncrement();
      return;
    }
    onChange?.(clamp(value + 1));
  };

  // ----------------------------------------------------

  const handleDecrement = () => {
    if (disabled || !canDecrement) return;
    if (onDecrement) {
      onDecrement();
      return;
    }
    onChange?.(clamp(value - 1));
  };

  // ----------------------------------------------------

  // Keep the input in sync with external value updates.
  useEffect(() => {
    setDraft(value === 0 && !showStepperWhenZero ? '' : String(value));
  }, [value, showStepperWhenZero]);

  // ----------------------------------------------------

  let rootSx = { ...styles.root };
  if (highlightWhenFilled && value > 0) {
    rootSx = {
      ...rootSx,
      backgroundColor: '#F5FAFF',
      border: '1px solid #55ACFF',
      boxShadow: 'none',
    };
  }
  if (rootStyles) rootSx = { ...rootSx, ...rootStyles };

  const stepColor =
    stepButtonColor ||
    (highlightWhenFilled ? 'primary.main' : 'secondary.main');

  return (
    <Box sx={rootSx}>
      {value === 0 && !showStepperWhenZero ? (
        <Button
          data-id={addDataId}
          type="button"
          onClick={handleIncrement}
          disabled={!canIncrement}
          aria-label="Add quantity"
          sx={{
            ...styles.addButton,
            color: canIncrement ? 'secondary.main' : 'grey.400',
          }}
        >
          Add
        </Button>
      ) : (
        <>
          <Button
            data-id={decrementDataId}
            type="button"
            onClick={handleDecrement}
            disabled={!canDecrement}
            aria-label="Decrement quantity"
            sx={{
              ...styles.stepButton,
              color: canDecrement ? stepColor : 'grey.400',
            }}
          >
            -
          </Button>

          <Box sx={styles.inputWrapper}>
            <InputBase
              value={draft}
              placeholder="Add"
              onChange={(e) => handleDraftChange(e.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit(draft);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              disabled={disabled}
              inputProps={{ 'aria-label': 'Quantity input', 'data-id': inputDataId }}
              sx={
                disabled
                  ? {
                      ...styles.inputBase,
                      color: 'grey.400',
                      '& input::placeholder': { opacity: 1, color: 'grey.400' },
                    }
                  : { ...styles.inputBase, color: 'secondary.main' }
              }
            />
          </Box>

          <Button
            data-id={incrementDataId}
            type="button"
            onClick={handleIncrement}
            disabled={!canIncrement}
            aria-label="Increment quantity"
            sx={{
              ...styles.stepButton,
              color: canIncrement ? stepColor : 'grey.400',
            }}
          >
            +
          </Button>
        </>
      )}
    </Box>
  );
};
